import { useMemo } from 'react';

import { getIcon } from '@/utils/getIcon';

import {
  StyledServiceRow,
  StyledServiceRowIcon,
  StyledLine,
  StyledServiceRowInfo,
  StyledTransferTypeText,
  StyledRouteText,
  StyledTimesText,
} from './styles';

import type { ItineraryProduct } from '@/types/products';

interface ItineraryServiceRowProps {
  itineraryProduct: ItineraryProduct;
  index: number;
  blocksLength: number;
}

export function ItineraryServiceRow({ itineraryProduct, index, blocksLength }: ItineraryServiceRowProps) {
  // Is used for converting the type of the product to a readable format. It splits the string at every
  // capital letter and adds a space in between. It also capitalizes the first letter of the first word and
  // decapitalizes the rest. For example 'carRental' will convert to 'Car rental'.
  const blockType = useMemo(
    () => (product: ItineraryProduct) => {
      const words = product.type.split(/(?=[A-Z])/).map((word) => word.toLowerCase());
      if (words.length === 0) return '';
      words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
      return words.join(' ');
    },
    [],
  );
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', day: 'numeric', month: 'long' }).format(date);
  };

  const icon = useMemo(() => (product: ItineraryProduct) => getIcon(product.type, '2xl'), []);

  return (
    <StyledServiceRow>
      <StyledServiceRowIcon>
        {icon(itineraryProduct)}
        {index !== blocksLength - 1 && <StyledLine />}
      </StyledServiceRowIcon>
      <StyledServiceRowInfo>
        <StyledTransferTypeText>{blockType(itineraryProduct)}</StyledTransferTypeText>
        {itineraryProduct.type.toLocaleLowerCase() !== 'flight' && (
          <StyledRouteText>
            {itineraryProduct.carouselName ?? itineraryProduct.nameForClient ?? itineraryProduct.name}
          </StyledRouteText>
        )}
        {itineraryProduct.type.toLocaleLowerCase() === 'flight' && (
          <StyledRouteText>
            {itineraryProduct.flight?.departureAirport} - {itineraryProduct.flight?.arrivalAirport}
          </StyledRouteText>
        )}
        {itineraryProduct.type.toLocaleLowerCase() === 'flight' && (
          <StyledTimesText>
            Departure {itineraryProduct.startTime} - Arrival {itineraryProduct.endTime}
          </StyledTimesText>
        )}
        {itineraryProduct.type.toLocaleLowerCase() === 'excursion' && (
          <StyledTimesText>On: {formatDate(itineraryProduct.startDate)}</StyledTimesText>
        )}
        {itineraryProduct.type.toLocaleLowerCase() === 'accommodation' && (
          <StyledTimesText>
            Check-in: {formatDate(itineraryProduct.startDate)}, Check-out: {formatDate(itineraryProduct.endDate)}
          </StyledTimesText>
        )}
        {itineraryProduct.type.toLocaleLowerCase() === 'carrental' && (
          <StyledTimesText>
            Pick-up: {formatDate(itineraryProduct.startDate)}, Drop-off: {formatDate(itineraryProduct.endDate)}
          </StyledTimesText>
        )}
      </StyledServiceRowInfo>
    </StyledServiceRow>
  );
}
